// A kiosk sale's outstanding amount, and a fresh UPI request for it (F-KIO-14; spec/12 §7.5, spec/06 §8.3, §9.3).
//
// A pass or a food order bought at the kiosk is a `commerce` sale with `channel: kiosk`. When the wallet is short the
// kiosk offers a **split**: "Use ₹X from wallet + pay the rest by UPI / at the desk" — the wallet part is a `wallet`
// tender on the sale, the rest a second tender on the same sale. The outstanding amount is the authority for what the
// second tender may be (spec/06 §8.3): never more.
//
//   GET  /v1/station/sales/{saleId}/outstanding   what the sale still owes, what the wallet paid, and its open QR
//   POST /v1/station/sales/{saleId}/upi           a new UPI request for what it still owes (Idempotency-Key); the
//                                                 previous open one is cancelled at the provider where possible
//                                                 (a sale has at most one open intent, spec/06 §9.3)
//
// Only a sale placed from **this** station is visible. Paid state is still derived from settled payments alone
// (`recomputeOrderState`, spec/06 §9.3) — nothing here marks a sale paid.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { SaleIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MoneySchema } from "../primitives/money.js";
import { SaleStatusSchema } from "./commerce.js";
import { IdempotentCreateHeaderSchema } from "./id-checks.js";
import { StationPassPurchaseUpiSchema } from "./passes.js";
const STATION_SALE_AUTH = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). Only a kiosk sale placed from this ' +
    'station is visible.';
/** What a kiosk sale still owes (F-KIO-14): the authority for a split tender's second part. */
export const StationSaleOutstandingSchema = z
    .object({
    saleId: SaleIdSchema,
    status: SaleStatusSchema,
    total: MoneySchema,
    /** Everything paid so far, by any tender. */
    paid: MoneySchema,
    /** What the wallet paid of it. */
    walletPaid: MoneySchema,
    /** `total − paid`, never below zero. */
    outstanding: MoneySchema,
    /** The sale's open UPI request, if it has one. */
    upi: StationPassPurchaseUpiSchema.nullable(),
})
    .openapi('StationSaleOutstanding', { description: 'What a kiosk sale still owes (F-KIO-14).' });
const StationSaleParamsSchema = z.object({ saleId: SaleIdSchema }).openapi('StationSaleParams');
export const getStationSaleOutstandingRoute = createRoute({
    method: 'get',
    path: '/v1/station/sales/{saleId}/outstanding',
    operationId: 'getStationSaleOutstanding',
    tags: ['commerce', 'stations'],
    summary: 'What a kiosk sale still owes (F-KIO-14)',
    description: `${STATION_SALE_AUTH} Returns the total, what has been paid (and how much of it by the wallet), what is outstanding and the open UPI request, if any.`,
    request: { params: StationSaleParamsSchema },
    responses: {
        200: jsonResponse(StationSaleOutstandingSchema, 'The sale’s outstanding amount.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('No such sale placed from this station.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const retryStationSaleUpiRoute = createRoute({
    method: 'post',
    path: '/v1/station/sales/{saleId}/upi',
    operationId: 'retryStationSaleUpi',
    tags: ['commerce', 'payments', 'stations'],
    summary: 'A new UPI request for what a kiosk sale still owes (F-KIO-14)',
    description: `${STATION_SALE_AUTH} Opens a payment intent for the outstanding amount and returns its QR. The sale’s previous ` +
        'open intent is cancelled first, at the provider where possible — a sale has at most one open intent (spec/06 ' +
        '§9.3). Requires an `Idempotency-Key`: a retry answers the same intent. Offered only when online payments are ' +
        'open at the venue. Audited.',
    request: { params: StationSaleParamsSchema, headers: IdempotentCreateHeaderSchema },
    responses: {
        200: jsonResponse(StationSaleOutstandingSchema, 'The sale, with its new UPI request.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('No such sale placed from this station.', 'not-found'),
        409: problemResponse('The sale is paid or cancelled, or online payments are not open here.', 'conflict', 'online-payments-unavailable'),
        422: problemResponse('The same key was already used for a different payment.', 'idempotency-key-reused', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=station-sales.js.map